import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Github, FileText, ArrowUpRight, TrendingUp, Activity } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { projects, projectFilters } from "@/data/portfolio";

const MiniSignal = ({ seed }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let running = false;
    let t = seed * 1.7;
    const draw = () => {
      if (!running) return;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      if (w === 0) {
        raf = requestAnimationFrame(draw);
        return;
      }
      if (canvas.width !== w * 2) {
        canvas.width = w * 2;
        canvas.height = h * 2;
      }
      ctx.setTransform(2, 0, 0, 2, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.beginPath();
      for (let x = 0; x <= w; x += 4) {
        const y =
          h / 2 +
          Math.sin(x * 0.025 + t) * h * 0.26 * Math.sin(x * 0.006 + seed) +
          Math.sin(x * 0.11 + t * 1.8) * h * 0.08;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, "rgba(6, 182, 212, 0.75)");
      grad.addColorStop(1, "rgba(139, 92, 246, 0.75)");
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      t += 0.035;
      raf = requestAnimationFrame(draw);
    };
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !running) {
        running = true;
        draw();
      } else if (!entry.isIntersecting && running) {
        running = false;
        cancelAnimationFrame(raf);
      }
    });
    io.observe(canvas);
    return () => {
      io.disconnect();
      running = false;
      cancelAnimationFrame(raf);
    };
  }, [seed]);
  return <canvas ref={ref} className="h-8 w-full" data-testid={`mini-signal-${seed}`} />;
};

export const Projects = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [preview, setPreview] = useState(null);
  const openedAtRef = useRef(0);
  useEffect(() => {
    if (active) openedAtRef.current = Date.now();
  }, [active]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
  const visible = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <section id="projects" className="relative py-24 lg:py-36" data-testid="projects-section">
      <div className="pointer-events-none absolute right-0 top-1/3 h-[500px] w-[500px] rounded-full orb-cyan" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">
              {"// 04 — Selected Work"}
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Projects with<br /><span className="gradient-text">measurable impact</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="projects-filter-tabs">
            {projectFilters.map((f) => (
              <button
                key={f}
                data-testid={`projects-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all duration-300 ${
                  filter === f
                    ? "border border-violet-400/50 bg-violet-500/15 text-violet-300"
                    : "border border-white/10 text-slate-400 hover:text-slate-200"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p, pi) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className={`group card-glass beam-card spotlight-card relative cursor-pointer overflow-hidden rounded-2xl border border-white/10 transition-all duration-500 hover:z-20 hover:border-cyan-400/40 hover:glow-cyan ${p.span}`}
                data-testid={`project-card-${p.id}`}
                title="Click to open the full case study"
                onClick={() => setActive(p)}
                onDoubleClick={() => setActive(p)}
                onMouseEnter={(e) => setPreview({ p, x: e.clientX, y: e.clientY })}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
                  setPreview({ p, x: e.clientX, y: e.clientY });
                  const inner = e.currentTarget.querySelector("[data-tilt]");
                  if (inner) {
                    const rx = ((e.clientY - rect.top) / rect.height - 0.5) * -8;
                    const ry = ((e.clientX - rect.left) / rect.width - 0.5) * 8;
                    inner.style.transform = `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-8px) scale(1.02)`;
                  }
                }}
                onMouseLeave={(e) => {
                  setPreview(null);
                  const inner = e.currentTarget.querySelector("[data-tilt]");
                  if (inner) {
                    inner.style.transform =
                      "perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0) scale(1)";
                  }
                }}
              >
              <div data-tilt className="transition-transform duration-300 ease-out will-change-transform">
                <div
                  className="relative h-52 cursor-pointer overflow-hidden sm:h-60"
                  onClick={() => setActive(p)}
                  data-testid={`project-image-${p.id}`}
                >
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E15] via-[#0D0E15]/30 to-transparent" />
                  <div className="absolute inset-0 bg-[#050508]/0 transition-colors duration-500 group-hover:bg-[#050508]/45" />
                  <div
                    className="shine-bar pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    style={{ animationDelay: `${pi * 0.65}s` }}
                  />
                  <span className="glass absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-emerald-400/30 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                    <TrendingUp size={11} /> {p.metric}
                  </span>
                  <span className="absolute right-4 top-4 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    {p.year}
                  </span>
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-xl font-bold text-slate-50">{p.title}</h3>
                      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-cyan-400">
                        {p.subtitle}
                      </p>
                    </div>
                    <button
                      data-testid={`project-details-${p.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActive(p);
                      }}
                      className="flex shrink-0 items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-500/10 px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-violet-300 transition-all duration-300 hover:bg-violet-500/30 hover:text-white"
                    >
                      More info <ArrowUpRight size={12} />
                    </button>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.description}</p>
                  <div className="mt-4 flex items-center gap-3 rounded-lg border border-white/5 bg-black/30 px-3 py-1.5">
                    <span className="flex shrink-0 items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-emerald-400">
                      <Activity size={11} className="animate-pulse" />
                      live
                    </span>
                    <MiniSignal seed={pi + 1} />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-white/5 px-2.5 py-1 font-mono text-[10px] tracking-wide text-slate-300"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-4 border-t border-white/10 pt-4">
                    {p.report && (
                    <a
                      data-testid={`project-report-${p.id}`}
                      href={p.report}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-400 transition-colors hover:text-cyan-300"
                    >
                      <FileText size={13} /> Report
                    </a>
                    )}
                    {p.github && p.github !== "#" && (
                      <a
                        data-testid={`project-github-${p.id}`}
                        href={p.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-400 transition-colors hover:text-violet-300"
                      >
                        <Github size={13} /> Code
                      </a>
                    )}
                  </div>
                </div>
              </div>
              </motion.article>
            ))}
          </AnimatePresence>
          {filter === "All" && (
            <motion.button
              layout
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              className="group card-glass relative flex min-h-[220px] flex-col items-start justify-between overflow-hidden rounded-2xl border border-dashed border-violet-500/40 p-6 text-left transition-all duration-500 hover:border-cyan-400/60 hover:glow-cyan md:col-span-2"
              data-testid="projects-cta-card"
            >
              <span className="font-mono text-[10px] uppercase tracking-widest text-violet-300">
                {"// More in the lab"}
              </span>
              <div>
                <h3 className="font-display text-xl font-bold text-slate-50">
                  Your project<br />could be next
                </h3>
                <span className="mt-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-cyan-400 transition-transform duration-300 group-hover:translate-x-1">
                  Let's talk <ArrowUpRight size={14} />
                </span>
              </div>
            </motion.button>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {preview && (
          <motion.div
            key={preview.p.id}
            initial={{ opacity: 0, scale: 0.88, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 8 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-none fixed z-[80] hidden w-[360px] overflow-hidden rounded-2xl border border-violet-500/40 bg-[#0D0E15]/95 shadow-[0_24px_80px_rgba(0,0,0,0.65)] backdrop-blur-xl lg:block"
            style={{
              left: Math.min(preview.x + 48, window.innerWidth - 400),
              top: Math.max(80, Math.min(preview.y - 60, window.innerHeight - 500)),
            }}
            data-testid="project-hover-preview"
          >
            <div className="relative h-44 overflow-hidden">
              <img src={preview.p.image} alt={preview.p.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E15] via-transparent to-transparent" />
              <span className="glass absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-emerald-400/30 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                <TrendingUp size={11} /> {preview.p.metric}
              </span>
            </div>
            <div className="p-5">
              <h4 className="font-display text-lg font-bold text-slate-50">{preview.p.title}</h4>
              <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-400">
                {preview.p.subtitle}
              </p>
              <p className="mt-3 line-clamp-3 text-xs leading-relaxed text-slate-400">
                {preview.p.longDescription}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {preview.p.tags.slice(0, 3).map((t) => (
                  <span key={t} className="rounded-full bg-white/5 px-2.5 py-1 font-mono text-[9px] text-slate-300">
                    {t}
                  </span>
                ))}
              </div>
              <p className="mt-4 flex items-center gap-2 border-t border-white/10 pt-3 font-mono text-[10px] font-bold uppercase tracking-widest text-violet-300">
                Click the card — full case study <ArrowUpRight size={12} />
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent
          className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#0D0E15] text-slate-200"
          data-testid="project-detail-modal"
          onInteractOutside={(e) => e.preventDefault()}
        >
          {active && (
            <>
              <div className="relative -mx-6 -mt-6 mb-2 h-56 overflow-hidden rounded-t-lg">
                <img src={active.image} alt={active.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E15] to-transparent" />
              </div>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-slate-50">
                  {active.title} <span className="ml-2 font-mono text-xs text-emerald-400">{active.metric}</span>
                </DialogTitle>
                <DialogDescription className="pt-3 text-sm leading-relaxed text-slate-400">
                  {active.longDescription}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap gap-2 pt-2">
                {active.tags.map((t) => (
                  <span key={t} className="rounded-full bg-white/5 px-3 py-1 font-mono text-[10px] text-cyan-300">
                    {t}
                  </span>
                ))}
              </div>

              <div className="flex gap-4 pt-4">
                {active.report && (
                <a
                  data-testid="project-modal-report-link"
                  href={active.report}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-full bg-violet-600 px-5 py-2.5 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-violet-500"
                >
                  <FileText size={14} /> Read Report (PDF)
                </a>
                )}
                {active.github && (
                  <a
                    data-testid="project-modal-github-link"
                    href={active.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-xs uppercase tracking-widest text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
                  >
                    <Github size={14} /> View Code
                  </a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
